import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Alert } from '@mui/material'

import LoadingScreen from '../../../components/LoadingScreen'
import TabsMui from '../../../layout/TabsMui'
import { canManagePocStoreRelations } from '../../../global_context/capabilities/pocCapabilities'
import {
  STORE_RELATIONS_ALERT_DURATION,
  STORE_RELATIONS_STATUS_UI_MAP
} from '../constants/storeConstants'
import {
  isStoreRelationClientInactive,
  resolveStoreRelationOverallStatus
} from '../helpers/storeRelationStatusUtils'
import { DEFAULT_PER_PAGE } from '../../../constants/constant'
import { fetchStoreRelationBrandSearch } from '../hooks/useStoreRelationBrandSearch'
import useFetchStoreHistory from '../hooks/useFetchStoreHistory'
import useFetchStoreShow from '../hooks/useFetchStoreShow'
import {
  STORE_RELATIONS_OVERVIEW_NOTE,
  STORE_RELATION_SHOW_STATUS_LABEL,
  STORE_RELATION_SHOW_TITLE,
  STORE_TABS,
  STORE_TAB_VALUES
} from './constants/storeShowConstants'
import {
  buildStoreRelationBrandValidationRows,
  buildStoreRelationReassignmentWarningMessages
} from './utils/storeShowUtils'
import StoreDetailsTab from './tabs/StoreDetailsTab'
import StoreHistoryTab from './tabs/StoreHistoryTab'

const ShowStoreView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(STORE_TAB_VALUES.DETAILS)
  const [historyPage, setHistoryPage] = useState(1)
  const [successAlertMessage, setSuccessAlertMessage] = useState('')
  const { data, loading } = useFetchStoreShow(id)
  const canWrite = canManagePocStoreRelations()
  const {
    data: historyData,
    loading: historyLoading,
    error: historyError
  } = useFetchStoreHistory(id, {
    enabled: activeTab === STORE_TAB_VALUES.HISTORY,
    page: historyPage,
    perPage: DEFAULT_PER_PAGE
  })
  const storeRelationData = data?.result || null
  const overallStatus = storeRelationData
    ? resolveStoreRelationOverallStatus(storeRelationData)
    : ''
  const statusUI = STORE_RELATIONS_STATUS_UI_MAP?.[overallStatus] || null
  const isPrincipalSuspended = storeRelationData?.client_principal?.status === 'suspended'
  const isClientInactive = isStoreRelationClientInactive(storeRelationData)
  const areEditActionsDisabled = isPrincipalSuspended || isClientInactive || !canWrite
  const reassignmentWarningMessages =
    buildStoreRelationReassignmentWarningMessages(storeRelationData)

  useEffect(() => {
    setHistoryPage(1)
  }, [id])

  useEffect(() => {
    const nextSuccessAlertMessage = location.state?.successAlertMessage

    if (!nextSuccessAlertMessage) return

    setSuccessAlertMessage(nextSuccessAlertMessage)

    const { successAlertMessage: _ignored, ...nextState } = location.state || {}

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null
      }
    )
  }, [location.hash, location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    if (!successAlertMessage) return undefined

    const timeoutId = setTimeout(() => {
      setSuccessAlertMessage('')
    }, STORE_RELATIONS_ALERT_DURATION)

    return () => clearTimeout(timeoutId)
  }, [successAlertMessage])

  const handleEditClick = async () => {
    if (areEditActionsDisabled) return

    const clientDbRef = storeRelationData?.client?.db_ref

    if (!storeRelationData || !clientDbRef) {
      navigate(`/store_relations/${id}/edit`, {
        state: {
          storeRelationData,
          returnTo: `/store_relations/${id}`
        }
      })
      return
    }

    const validationRows = buildStoreRelationBrandValidationRows(storeRelationData)

    const validationResults = await Promise.all(
      validationRows.map(async (row) => {
        try {
          const result = await fetchStoreRelationBrandSearch({
            clientDbRef,
            salesmanDbRef: row.salesmanDbRef,
            searchTerm: '',
            currentBrandDbRefs: row.brandDbRefs
          })

          const unavailableBrandDbRefs = (result.meta?.currentBrandsStatus || [])
            .filter(
              (item) =>
                item?.available === false || ['removed', 'unavailable'].includes(item?.status)
            )
            .map((item) => item?.db_ref)
            .filter(Boolean)

          if (!unavailableBrandDbRefs.length) return null

          return `El usuario ${row.userLabel} tiene marcas no disponibles: ${unavailableBrandDbRefs.join(', ')}`
        } catch (error) {
          console.error('brand validation error', error)
          return null
        }
      })
    )

    navigate(`/store_relations/${id}/edit`, {
      state: {
        storeRelationData,
        returnTo: `/store_relations/${id}`,
        brandValidationAlerts: validationResults.filter(Boolean)
      }
    })
  }

  if (loading) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      {successAlertMessage && (
        <div className="store-relations-page-alert">
          <Alert severity="success" onClose={() => setSuccessAlertMessage('')}>
            {successAlertMessage}
          </Alert>
        </div>
      )}

      {reassignmentWarningMessages.length > 0 && activeTab === STORE_TAB_VALUES.DETAILS && (
        <div className="store-relations-form-page-alert">
          <Alert
            severity="warning"
            sx={{
              width: '100%',
              borderRadius: 0,
              mb: 2
            }}
          >
            <ol className="store-relations-form-page-alert__list">
              {reassignmentWarningMessages.map((message) => (
                <li key={message} className="store-relations-form-page-alert__list-item">
                  {message}
                </li>
              ))}
            </ol>
          </Alert>
        </div>
      )}

      <div className="main-show-header">
        <h2>{STORE_RELATION_SHOW_TITLE}</h2>
      </div>

      <div className="index-table">
        <div className="show-area">
          <div className="store-relations-status-bar">
            <div className="status-orders">
              <b className="order-status-b">{STORE_RELATION_SHOW_STATUS_LABEL}</b>
              <span className={`oc-order-status ${statusUI?.className || ''}`}>
                {statusUI?.label || overallStatus || '-'}
              </span>
            </div>
          </div>

          <div className="store-relations-tabs-header">
            <div className="store-relations-tabs-nav">
              <TabsMui
                tabs={STORE_TABS}
                activeTab={activeTab}
                onChange={setActiveTab}
                showDivider={false}
                marginBottom={0}
              />
            </div>
          </div>

          <hr className="store-relations-tabs-divider" />

          {activeTab === STORE_TAB_VALUES.DETAILS && (
            <p className="store-relations-overview-note">
              <strong>Nota:</strong> {STORE_RELATIONS_OVERVIEW_NOTE}
            </p>
          )}

          <div className="tab-content-area">
            {activeTab === STORE_TAB_VALUES.DETAILS && (
              <StoreDetailsTab
                data={storeRelationData}
                onEdit={handleEditClick}
                disableEditActions={areEditActionsDisabled}
              />
            )}
            {activeTab === STORE_TAB_VALUES.HISTORY && (
              <StoreHistoryTab
                entries={historyData?.results || []}
                loading={historyLoading}
                error={historyError}
                currentPage={historyPage}
                perPage={DEFAULT_PER_PAGE}
                totalRecords={historyData?.total_records || 0}
                onPageChange={setHistoryPage}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ShowStoreView
